import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Newspaper, Mail } from "lucide-react";

/**
 * Public press release page. Reachable ONLY by knowing the exact slug —
 * there is no `/press` index and every rendered release ships `noindex,
 * nofollow` meta so it's invisible to search engines. Unpublished drafts
 * 404 for the public.
 */
export default function PublicPress() {
  const { slug } = useParams();
  const [release, setRelease] = useState(null);
  const [error, setError] = useState("");

  // Inject `noindex, nofollow` for the lifetime of this page and unwind on
  // unmount so we don't affect any other route.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    return () => {
      try { document.head.removeChild(meta); } catch { /* already removed */ }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError("");
    setRelease(null);
    http.get(`/press/${encodeURIComponent(slug)}`)
      .then((r) => { if (!cancelled) setRelease(r.data); })
      .catch((e) => {
        if (cancelled) return;
        if (e.response?.status === 404) {
          setError("This press release either doesn't exist or has been unpublished.");
        } else {
          setError(formatApiErrorDetail(e) || "Could not load this release.");
        }
      });
    return () => { cancelled = true; };
  }, [slug]);

  const publishedDate = release?.published_at
    ? new Date(release.published_at).toLocaleDateString(undefined, {
        day: "numeric", month: "long", year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="public-press-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" aria-label="Affra home">
            <Logo size="h-8" />
          </Link>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-indigo-600">
            <Newspaper className="w-3.5 h-3.5" />
            Press
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        {error ? (
          <div className="text-center py-16">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">404</div>
            <h1 className="mt-3 font-display font-black text-4xl tracking-tighter">Not found</h1>
            <p className="mt-4 text-zinc-500 max-w-lg mx-auto">{error}</p>
            <Link to="/" className="mt-8 inline-block text-sm text-indigo-600 hover:underline">
              ← Back to Affra
            </Link>
          </div>
        ) : !release ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : (
          <article className="prose max-w-none" data-testid="press-article">
            <div className="text-xs font-mono uppercase tracking-widest text-indigo-600 not-prose">
              For immediate release{publishedDate ? ` · ${publishedDate}` : ""}
            </div>
            <h1
              className="mt-4 font-display font-black text-4xl lg:text-5xl leading-[1.05] tracking-tighter not-prose"
              data-testid="press-title"
            >
              {release.title}
            </h1>
            <div
              className="mt-8 space-y-4 text-[17px] leading-[1.75] text-zinc-800 press-body"
              data-testid="press-body"
            >
              <ReactMarkdown
                components={{
                  h1: ({ node, ...p }) => <h2 className="mt-10 mb-3 font-display font-bold text-2xl tracking-tight" {...p} />,
                  h2: ({ node, ...p }) => <h3 className="mt-8 mb-3 font-display font-bold text-xl tracking-tight" {...p} />,
                  h3: ({ node, ...p }) => <h4 className="mt-6 mb-2 font-display font-bold text-lg tracking-tight" {...p} />,
                  p:  ({ node, ...p }) => <p className="my-4 text-[17px] leading-[1.75]" {...p} />,
                  ul: ({ node, ...p }) => <ul className="my-4 pl-6 list-disc space-y-1.5" {...p} />,
                  ol: ({ node, ...p }) => <ol className="my-4 pl-6 list-decimal space-y-1.5" {...p} />,
                  blockquote: ({ node, ...p }) => (
                    <blockquote className="my-6 pl-5 border-l-4 border-indigo-600 text-zinc-700 italic" {...p} />
                  ),
                  a: ({ node, ...p }) => (
                    <a {...p} target="_blank" rel="noopener noreferrer nofollow"
                       className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2" />
                  ),
                  code: ({ node, ...p }) => (
                    <code className="px-1 py-0.5 rounded bg-zinc-100 text-[15px] font-mono" {...p} />
                  ),
                  hr: () => <hr className="my-10 border-zinc-200" />,
                }}
              >
                {release.body || ""}
              </ReactMarkdown>
            </div>

            <hr className="my-12 border-zinc-200 not-prose" />
            <section className="not-prose text-sm text-zinc-600 leading-relaxed">
              <div className="font-display font-bold text-zinc-900 mb-2">Media contact</div>
              <div>
                <a
                  href="mailto:press@affra.io"
                  className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700"
                >
                  <Mail className="w-3.5 h-3.5" /> press@affra.io
                </a>
              </div>
              <p className="mt-4 text-zinc-500 text-xs">
                Affra is a fully white-label ticketing platform for venues and promoters —
                0% payment processing on every ticket and £0 monthly fees, forever.
              </p>
            </section>
          </article>
        )}
      </main>

      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-3xl mx-auto px-6 flex items-center justify-between text-xs text-zinc-500">
          <div>© {new Date().getFullYear()} Affra</div>
          <Link to="/" className="hover:text-zinc-900">affra.io</Link>
        </div>
      </footer>
    </div>
  );
}
